import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Sparkles, CheckCircle2, AlertTriangle, Clock, Send, Inbox as InboxIcon, User, Headset, ExternalLink, Wand2, Plus, X, ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown, Archive, ArchiveRestore, History, Search, GitMerge, Gauge, Ban, Zap, ShieldAlert, Bot, Settings2, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '../components/ui/dropdown-menu';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { useSession } from '@clerk/react';
import axiosInstance from '../utils/axiosInstance';
import config from '../config';

const STATUS_COLORS = {
  ready:    { bg: 'bg-emerald-50',  text: 'text-emerald-700',  icon: CheckCircle2, label: 'ready to send' },
  partial:  { bg: 'bg-amber-50',    text: 'text-amber-700',    icon: AlertTriangle, label: 'partial data' },
  missing:  { bg: 'bg-rose-50',     text: 'text-rose-700',     icon: AlertTriangle, label: 'endpoint missing' },
  pending:  { bg: 'bg-stone-100',   text: 'text-stone-600',    icon: Clock, label: 'not classified' },
  human:    { bg: 'bg-indigo-50',   text: 'text-indigo-700',   icon: User, label: 'human review' },
  error:    { bg: 'bg-rose-50',     text: 'text-rose-700',     icon: AlertTriangle, label: 'dispatch error' },
};
const UNKNOWN_STATUS = {
  bg: 'bg-stone-100',
  text: 'text-stone-600',
  icon: Clock,
  label: 'unknown',
};

const CategoryPill = ({ name }) => (
  <span className="pill">{name || 'uncategorized'}</span>
);

const Inbox = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [viewId, setViewId] = useState(149731);
  const [dispatching, setDispatching] = useState(false);
  const [ingestEvents, setIngestEvents] = useState([]);
  const [ingestSummary, setIngestSummary] = useState(null);
  const [ingestPanelOpen, setIngestPanelOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pollOn, setPollOn] = useState(true);
  const [lastPollAt, setLastPollAt] = useState(null);
  const [autoAssign, setAutoAssign] = useState(false);
  const [autoFixCustomer, setAutoFixCustomer] = useState(false);
  const [bulkFixing, setBulkFixing] = useState(false);
  const [dispatchingAll, setDispatchingAll] = useState(false);
  const [dispatchAllEvents, setDispatchAllEvents] = useState([]);
  const [dispatchAllSummary, setDispatchAllSummary] = useState(null);
  const [dispatchAllPanelOpen, setDispatchAllPanelOpen] = useState(false);
  const [autoPostOnDispatch, setAutoPostOnDispatch] = useState(false);
  const [onlyMissingDispatch, setOnlyMissingDispatch] = useState(true);
  const ingestAbortRef = useRef(null);
  const dispatchAllAbortRef = useRef(null);
  const { session } = useSession();

  const loadTickets = async () => {
    setLoading(true);
    try {
      const archived = showArchived ? '1' : '0';
      const params = new URLSearchParams({ archived, status: statusFilter });
      if (searchQuery.trim()) params.set('q', searchQuery.trim());
      const { data } = await axiosInstance.get(`api/replai/tickets/?${params}`);
      setTickets(data.results || data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const ingest = async () => {
    if (!session) return;
    setIngesting(true);
    setIngestEvents([]);
    setIngestSummary(null);
    setIngestPanelOpen(true);

    const controller = new AbortController();
    ingestAbortRef.current = controller;

    try {
      const token = await session.getToken();
      const base = config.apiUrl.endsWith('/') ? config.apiUrl : `${config.apiUrl}/`;
      const response = await fetch(`${base}api/replai/ingest/stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ view_id: viewId }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newlineIdx;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIdx).trim();
          buffer = buffer.slice(newlineIdx + 1);
          if (!line) continue;
          try {
            const event = JSON.parse(line);
            if (event.type === 'done') {
              setIngestSummary(event);
            } else if (event.type === 'fatal') {
              setIngestEvents((prev) => [...prev, { ...event, _key: Date.now() + Math.random() }]);
            } else {
              setIngestEvents((prev) => [...prev, { ...event, _key: Date.now() + Math.random() }]);
            }
          } catch (err) {
            console.error('Failed to parse ingest event', line, err);
          }
        }
      }
      await loadTickets();
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error(e);
      }
    } finally {
      setIngesting(false);
      ingestAbortRef.current = null;
    }
  };

  const cancelIngest = () => {
    if (ingestAbortRef.current) {
      ingestAbortRef.current.abort();
    }
  };

  const dispatchAll = async ({ autoPost } = {}) => {
    if (!session) return;
    setDispatchingAll(true);
    setDispatchAllEvents([]);
    setDispatchAllSummary(null);
    setDispatchAllPanelOpen(true);

    const controller = new AbortController();
    dispatchAllAbortRef.current = controller;

    try {
      const token = await session.getToken();
      const base = config.apiUrl.endsWith('/') ? config.apiUrl : `${config.apiUrl}/`;
      const response = await fetch(`${base}api/replai/dispatch-all/stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          auto_post: !!autoPost,
          only_missing: onlyMissingDispatch,
        }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newlineIdx;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIdx).trim();
          buffer = buffer.slice(newlineIdx + 1);
          if (!line) continue;
          try {
            const event = JSON.parse(line);
            if (event.type === 'done') {
              setDispatchAllSummary(event);
            } else {
              setDispatchAllEvents((prev) => [...prev, { ...event, _key: Date.now() + Math.random() }]);
            }
          } catch (err) {
            console.error('Failed to parse dispatch event', line, err);
          }
        }
      }
      await loadTickets();
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error(e);
      }
    } finally {
      setDispatchingAll(false);
      dispatchAllAbortRef.current = null;
    }
  };

  const cancelDispatchAll = () => {
    if (dispatchAllAbortRef.current) {
      dispatchAllAbortRef.current.abort();
    }
  };

  const loadDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const { data } = await axiosInstance.get(`api/replai/tickets/${id}/`);
      setDetail(data);
    } catch (e) {
      console.error(e);
    }
  };

  const dispatchTicket = async () => {
    if (!selectedId) return;
    setDispatching(true);
    try {
      await axiosInstance.post(`api/replai/tickets/${selectedId}/dispatch/`);
      await loadDetail(selectedId);
      await loadTickets();
    } catch (e) {
      console.error(e);
    } finally {
      setDispatching(false);
    }
  };

  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(null);
  const [savingFeedback, setSavingFeedback] = useState(false);

  const submitFeedback = async (feedback, note = '') => {
    if (!detail?.dispatch_run_id) return;
    setSavingFeedback(true);
    try {
      await axiosInstance.patch(
        `api/replai/dispatch-runs/${detail.dispatch_run_id}/feedback/`,
        { feedback, note },
      );
      await loadDetail(selectedId);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingFeedback(false);
    }
  };

  const blockSender = async (scope = 'domain') => {
    if (!selectedId || !detail) return;
    const target = scope === 'domain'
      ? (detail.customer_email || '').split('@')[1]
      : detail.customer_email;
    const ok = window.confirm(
      `Block ${scope === 'domain' ? 'everything from @' + target : target}? ` +
      'All matching tickets (this one and any others) will be archived immediately.'
    );
    if (!ok) return;
    try {
      const { data } = await axiosInstance.post(
        `api/replai/tickets/${selectedId}/block-sender/`,
        { scope }
      );
      setSelectedId(null);
      setDetail(null);
      await loadTickets();
      window.alert(
        `Blocked ${data.entry}. Archived ${data.archived_now} matching ticket${data.archived_now === 1 ? '' : 's'}.`
      );
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || e.message || 'Failed to block sender');
    }
  };

  const archiveCurrent = async () => {
    if (!selectedId) return;
    try {
      await axiosInstance.post(`api/replai/tickets/${selectedId}/archive/`);
      setSelectedId(null);
      setDetail(null);
      await loadTickets();
    } catch (e) {
      console.error(e);
    }
  };

  const [fixingCustomer, setFixingCustomer] = useState(false);

  const fixCustomerInGorgias = async () => {
    if (!selectedId) return;
    const ok = window.confirm(
      `Fix the customer on Gorgias ticket to ${detail?.customer_email}? ` +
      'This writes to Gorgias and rebinds the ticket to the real customer contact.'
    );
    if (!ok) return;
    setFixingCustomer(true);
    try {
      await axiosInstance.post(`api/replai/tickets/${selectedId}/fix-customer/`);
      await loadDetail(selectedId);
    } catch (e) {
      console.error(e);
      window.alert(
        e.response?.data?.error || e.message || 'Failed to fix customer in Gorgias'
      );
    } finally {
      setFixingCustomer(false);
    }
  };

  const assignCurrent = async (agentName) => {
    if (!selectedId || !agentName) return;
    const ok = window.confirm(
      `Assign this Gorgias ticket to ${agentName}? This writes to Gorgias.`
    );
    if (!ok) return;
    try {
      await axiosInstance.post(`api/replai/tickets/${selectedId}/assign/`, {
        agent_name: agentName,
      });
      await loadDetail(selectedId);
    } catch (e) {
      console.error(e);
      window.alert(
        e.response?.data?.error ||
          e.message ||
          'Failed to assign the ticket in Gorgias'
      );
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(tickets.map((t) => t.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkArchive = async (unarchive = false) => {
    if (selectedIds.size === 0) return;
    const ok = window.confirm(
      `${unarchive ? 'Unarchive' : 'Archive'} ${selectedIds.size} ticket${selectedIds.size === 1 ? '' : 's'}?`
    );
    if (!ok) return;
    try {
      await axiosInstance.post('api/replai/tickets/bulk-archive/', {
        ids: Array.from(selectedIds),
        unarchive,
      });
      clearSelection();
      await loadTickets();
    } catch (e) {
      console.error(e);
    }
  };

  const postNote = async () => {
    if (!selectedId) return;
    const ok = window.confirm(
      'Post the current draft as an internal note on the Gorgias ticket? ' +
      'Agents will see it in the thread - customers will NOT.'
    );
    if (!ok) return;
    setPosting(true);
    setPostError(null);
    try {
      await axiosInstance.post(`api/replai/tickets/${selectedId}/post-note/`);
      await loadDetail(selectedId);
    } catch (e) {
      console.error(e);
      setPostError(e.response?.data?.error || e.message || 'Failed to post note');
    } finally {
      setPosting(false);
    }
  };

  useEffect(() => {
    loadTickets();
    clearSelection();
  }, [showArchived, statusFilter]);

  // Debounce search so we don't hammer the API on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => loadTickets(), 250);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // Pull the auto-assign + auto-fix toggles once on mount.
  useEffect(() => {
    axiosInstance
      .get('api/replai/settings/')
      .then(({ data }) => {
        setAutoAssign(Boolean(data?.auto_assign_enabled));
        setAutoFixCustomer(Boolean(data?.auto_fix_customer_enabled));
      })
      .catch(() => {});
  }, []);

  const toggleAutoAssign = async () => {
    const next = !autoAssign;
    setAutoAssign(next);
    try {
      await axiosInstance.patch('api/replai/settings/', { auto_assign_enabled: next });
    } catch (e) {
      console.error(e);
      setAutoAssign(!next);
    }
  };

  const toggleAutoFixCustomer = async () => {
    const next = !autoFixCustomer;
    setAutoFixCustomer(next);
    try {
      await axiosInstance.patch('api/replai/settings/', { auto_fix_customer_enabled: next });
    } catch (e) {
      console.error(e);
      setAutoFixCustomer(!next);
    }
  };

  const bulkFixCustomers = async () => {
    const ok = window.confirm(
      'Run "Fix customer in Gorgias" on every mismatched ticket? ' +
      'This writes to Gorgias for each ticket that still has a placeholder customer.'
    );
    if (!ok) return;
    setBulkFixing(true);
    try {
      const { data } = await axiosInstance.post('api/replai/tickets/bulk-fix-customer/');
      window.alert(
        `Fixed ${data.fixed} · skipped ${data.skipped}` +
        (data.errors?.length ? ` · ${data.errors.length} errors` : '')
      );
      await loadTickets();
      if (selectedId) await loadDetail(selectedId);
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || e.message || 'Bulk fix failed');
    } finally {
      setBulkFixing(false);
    }
  };

  const mergeInto = async (targetTicketId) => {
    if (!selectedId) return;
    const ok = window.confirm(
      `Merge the current ticket into ticket #${targetTicketId}? ` +
      'This writes to Gorgias: the current ticket becomes a child of the target, ' +
      'and is archived locally.'
    );
    if (!ok) return;
    try {
      await axiosInstance.post(`api/replai/tickets/${selectedId}/merge/`, {
        target_ticket_id: targetTicketId,
      });
      setSelectedId(null);
      setDetail(null);
      await loadTickets();
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.error || e.message || 'Merge failed');
    }
  };

  // Lightweight periodic poll: hits the (non-streaming) ingest endpoint
  // every 60 seconds and refreshes the list if anything new arrived.
  // Toggleable via `pollOn`. Skipped while the user is actively
  // ingesting to avoid clobbering the event stream.
  useEffect(() => {
    if (!pollOn || !session) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (ingesting || cancelled) return;
      try {
        const { data } = await axiosInstance.post('api/replai/ingest/', {
          view_id: Number(viewId),
        });
        setLastPollAt(new Date());
        if ((data?.created || 0) + (data?.updated || 0) > 0) {
          await loadTickets();
        }
      } catch (e) {
        // silent: polling failures are non-fatal, don't spam the user
      }
    };
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pollOn, viewId, ingesting, showArchived, statusFilter, session]);

  const navIds = useMemo(() => {
    if (!selectedId || tickets.length === 0) return { prevId: null, nextId: null, position: null };
    const idx = tickets.findIndex((t) => t.id === selectedId);
    if (idx === -1) return { prevId: null, nextId: null, position: null };
    return {
      prevId: idx > 0 ? tickets[idx - 1].id : null,
      nextId: idx < tickets.length - 1 ? tickets[idx + 1].id : null,
      position: `${idx + 1} of ${tickets.length}`,
    };
  }, [selectedId, tickets]);

  const goPrev = () => { if (navIds.prevId) loadDetail(navIds.prevId); };
  const goNext = () => { if (navIds.nextId) loadDetail(navIds.nextId); };

  // Keyboard shortcuts: ← / → to navigate, j / k as vim-style alternates.
  // Ignored while typing in an input/textarea so we don't hijack the viewId
  // field or macro keyword editors elsewhere.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft' || e.key === 'k') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' || e.key === 'j') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navIds.prevId, navIds.nextId]);

  const counts = useMemo(() => {
    const c = { total: tickets.length, ready: 0, partial: 0, missing: 0, pending: 0 };
    tickets.forEach((t) => { c[t.dispatch_status || 'pending'] = (c[t.dispatch_status || 'pending'] || 0) + 1; });
    return c;
  }, [tickets]);

  const header = (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={viewId}
        onChange={(e) => setViewId(e.target.value)}
        className="h-9 w-28 rounded-xl border border-border bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
        placeholder="view id"
        title="Gorgias view id"
      />
      <Button
        onClick={ingest}
        disabled={ingesting}
        size="sm"
        className="gap-2 h-9"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${ingesting ? 'animate-spin' : ''}`} />
        Ingest
      </Button>

      {/* Dispatch - primary action with options in a split dropdown */}
      <DropdownMenu>
        <div className="inline-flex items-stretch">
          <Button
            onClick={() => dispatchAll({ autoPost: autoPostOnDispatch })}
            disabled={dispatchingAll}
            variant="secondary"
            size="sm"
            className="gap-2 h-9 rounded-r-none"
            title="Run the agent on inbox tickets. Options in the chevron menu."
          >
            <Zap className={`h-3.5 w-3.5 ${dispatchingAll ? 'animate-pulse' : ''}`} />
            Dispatch all
          </Button>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              className="h-9 px-2 rounded-l-none border-l border-[hsl(var(--background))]"
              disabled={dispatchingAll}
              title="Dispatch options"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
        </div>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Dispatch options</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={onlyMissingDispatch}
            onCheckedChange={setOnlyMissingDispatch}
          >
            <div className="flex flex-col">
              <span>Only new tickets</span>
              <span className="text-[0.65rem] text-muted-foreground">Skip tickets that already have a run</span>
            </div>
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={autoPostOnDispatch}
            onCheckedChange={setAutoPostOnDispatch}
          >
            <div className="flex flex-col">
              <span>Push ready drafts</span>
              <span className="text-[0.65rem] text-muted-foreground">Auto-post drafts ≥ confidence threshold</span>
            </div>
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Settings (gear) - auto-assign, auto-fix, fix-all */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-9 p-0" title="Settings">
            <Settings2 className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Ingestion settings</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={autoAssign}
            onCheckedChange={() => toggleAutoAssign()}
          >
            <div className="flex flex-col">
              <span>Auto-assign</span>
              <span className="text-[0.65rem] text-muted-foreground">Route new tickets to prior agent</span>
            </div>
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={autoFixCustomer}
            onCheckedChange={() => toggleAutoFixCustomer()}
          >
            <div className="flex flex-col">
              <span>Auto-fix customer</span>
              <span className="text-[0.65rem] text-muted-foreground">Rebind placeholder in Gorgias</span>
            </div>
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              bulkFixCustomers();
            }}
            disabled={bulkFixing}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${bulkFixing ? 'animate-spin' : ''}`} />
            <div className="flex flex-col">
              <span>{bulkFixing ? 'Fixing…' : 'Fix all placeholder customers'}</span>
              <span className="text-[0.65rem] text-muted-foreground">Runs the rebind on every open ticket</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <AppShell
      title="Inbox"
      subtitle="Tickets from Gorgias view, classified and staged for reply."
      headerRight={header}
    >
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard label="Total"   value={counts.total}   tone="neutral" />
        <StatCard label="Ready"   value={counts.ready}   tone="success" />
        <StatCard label="Partial" value={counts.partial} tone="warning" />
        <StatCard label="Missing data" value={counts.missing} tone="danger" />
      </div>

      {ingestPanelOpen && (
        <IngestProgressPanel
          ingesting={ingesting}
          events={ingestEvents}
          summary={ingestSummary}
          onClose={() => setIngestPanelOpen(false)}
          onCancel={cancelIngest}
        />
      )}

      {dispatchAllPanelOpen && (
        <DispatchAllProgressPanel
          dispatching={dispatchingAll}
          events={dispatchAllEvents}
          summary={dispatchAllSummary}
          onClose={() => setDispatchAllPanelOpen(false)}
          onCancel={cancelDispatchAll}
        />
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6 h-[calc(100vh-14rem)]">
        <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col h-full">
          <div className="px-5 py-3 border-b border-border/70 flex items-center justify-between shrink-0 gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground min-w-0">
              <InboxIcon className="h-4 w-4 shrink-0" />
              <span className="shrink-0">{tickets.length}</span>
              <button
                type="button"
                onClick={() => setStatusFilter((v) => (v === 'open' ? 'all' : 'open'))}
                className={`shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold transition ${
                  statusFilter === 'open'
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-stone-100 text-stone-700'
                }`}
                title={statusFilter === 'open' ? 'Showing only open tickets - click for all' : 'Showing all statuses - click for open only'}
              >
                {statusFilter === 'open' ? 'open only' : 'all statuses'}
              </button>
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className={`shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold transition ${
                  showArchived
                    ? 'bg-stone-200 text-stone-700'
                    : 'text-muted-foreground hover:bg-[hsl(var(--secondary))]'
                }`}
                title={showArchived ? 'Showing archived' : 'Showing active'}
              >
                {showArchived ? 'archived' : 'active'}
              </button>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {pollOn && (
                <span
                  className="text-[0.6rem] text-muted-foreground tabular-nums"
                  title={lastPollAt ? `Last checked ${lastPollAt.toLocaleTimeString()}` : 'Polling every 60s'}
                >
                  auto
                </span>
              )}
              {loading && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>
          <div className="px-5 py-2 border-b border-border/70 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search subject, customer, body…"
                className="h-9 w-full rounded-lg border border-border bg-white pl-8 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
                  title="Clear"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          {selectedIds.size > 0 && (
            <div className="px-5 py-2 border-b border-border/70 bg-[hsl(var(--accent))] flex items-center gap-3 text-xs">
              <span className="font-medium text-foreground">{selectedIds.size} selected</span>
              <button
                type="button"
                onClick={() => bulkArchive(showArchived)}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium hover:bg-white"
              >
                {showArchived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
                {showArchived ? 'Unarchive' : 'Archive'}
              </button>
              <button
                type="button"
                onClick={selectAllVisible}
                className="text-muted-foreground hover:text-foreground"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="text-muted-foreground hover:text-foreground ml-auto"
              >
                Clear
              </button>
            </div>
          )}
          <ul className="divide-y divide-border/70 flex-1 overflow-y-auto">
            {tickets.length === 0 && !loading && (
              <li className="px-5 py-10 text-center text-sm text-muted-foreground">
                No tickets yet. Ingest a view to get started.
              </li>
            )}
            {tickets.map((t) => {
              const s = STATUS_COLORS[t.dispatch_status || 'pending'] || UNKNOWN_STATUS;
              const Icon = s.icon;
              const active = selectedId === t.id;
              const isSelected = selectedIds.has(t.id);
              return (
                <li
                  key={t.id}
                  onClick={() => loadDetail(t.id)}
                  className={`px-5 py-4 cursor-pointer transition ${
                    active ? 'bg-[hsl(var(--accent))]' : 'hover:bg-[hsl(var(--secondary))]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(t.id)}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-border"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{t.subject || '(no subject)'}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            <span className="font-mono">#RPL-{t.id}</span>
                            {' · '}
                            {t.customer_email}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${s.bg} ${s.text}`}>
                            <Icon className="h-3 w-3" /> {s.label}
                          </span>
                          {t.draft_confidence != null && (
                            <span
                              className={`text-[0.65rem] font-semibold tabular-nums ${
                                t.draft_confidence >= 0.9
                                  ? 'text-emerald-700'
                                  : t.draft_confidence >= 0.7
                                  ? 'text-amber-700'
                                  : 'text-rose-700'
                              }`}
                              title="Replai self-reported confidence in the draft"
                            >
                              {(t.draft_confidence * 100).toFixed(0)}%
                            </span>
                          )}
                          {(t.last_message_at || t.created_at_gorgias) && (
                            <span
                              className="text-[0.65rem] text-muted-foreground tabular-nums"
                              title={new Date(t.last_message_at || t.created_at_gorgias).toLocaleString()}
                            >
                              {formatRelative(t.last_message_at || t.created_at_gorgias)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <CategoryPill name={t.category} />
                        {t.suggested_agent_name && (
                          <span
                            className="inline-flex items-center gap-1 text-[0.65rem] text-muted-foreground"
                            title={`Previously handled by ${t.suggested_agent_name}`}
                          >
                            <History className="h-3 w-3" />
                            {t.suggested_agent_name}
                          </span>
                        )}
                        {(t.tags || []).slice(0, 3).map((tag) => (
                          <span key={tag} className="text-[0.65rem] text-muted-foreground">#{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="bg-white rounded-2xl border border-border shadow-sm p-6 h-full overflow-y-auto">
          {!selectedId && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-muted-foreground">
              <Sparkles className="h-8 w-8 text-[hsl(var(--primary))]" />
              <p className="font-display text-xl text-foreground">Pick a ticket</p>
              <p className="text-sm">Replai will show the classification, the data pulled, and the draft reply.</p>
            </div>
          )}
          {selectedId && !detail && (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          )}
          {detail && (
            <TicketDetail
              detail={detail}
              onDispatch={dispatchTicket}
              dispatching={dispatching}
              onPostNote={postNote}
              posting={posting}
              postError={postError}
              onPrev={goPrev}
              onNext={goNext}
              hasPrev={Boolean(navIds.prevId)}
              hasNext={Boolean(navIds.nextId)}
              position={navIds.position}
              onFeedback={submitFeedback}
              savingFeedback={savingFeedback}
              onArchive={archiveCurrent}
              onAssign={assignCurrent}
              onFixCustomer={fixCustomerInGorgias}
              fixingCustomer={fixingCustomer}
              onMerge={mergeInto}
              onBlockSender={blockSender}
            />
          )}
        </section>
      </div>
    </AppShell>
  );
};

const StatCard = ({ label, value, tone }) => {
  const map = {
    neutral: 'bg-white border-border',
    success: 'bg-emerald-50/60 border-emerald-200/70',
    warning: 'bg-amber-50/60 border-amber-200/70',
    danger:  'bg-rose-50/60 border-rose-200/70',
  };
  return (
    <div className={`rounded-2xl border ${map[tone]} p-4`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display text-3xl font-semibold mt-1">{value}</p>
    </div>
  );
};

const TicketDetail = ({
  detail,
  onDispatch,
  dispatching,
  onPostNote,
  posting,
  postError,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  position,
  onFeedback,
  savingFeedback,
  onArchive,
  onAssign,
  onFixCustomer,
  fixingCustomer,
  onMerge,
  onBlockSender,
}) => {
  const gorgiasEmail = (detail.gorgias_customer_email || '').toLowerCase();
  const localEmail = (detail.customer_email || '').toLowerCase();
  const gorgiasIsPlaceholder =
    !gorgiasEmail ||
    gorgiasEmail.endsWith('@lamptwist.be') ||
    gorgiasEmail.includes('emails.gorgias.com') ||
    gorgiasEmail.endsWith('@bot.gorgias.com');
  const customerMismatch =
    gorgiasIsPlaceholder && localEmail && localEmail !== gorgiasEmail;
  const status = detail.dispatch_status || 'pending';
  const color = STATUS_COLORS[status] || UNKNOWN_STATUS;
  const Icon = color.icon;
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-semibold text-foreground">{detail.subject}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-mono text-[0.7rem] bg-[hsl(var(--secondary))] text-foreground rounded px-1.5 py-0.5">
              #RPL-{detail.id}
            </span>
            {' · '}
            From <span className="text-foreground font-medium">{detail.customer_email}</span>
            {' · '}
            {detail.language?.toUpperCase()}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            title="Previous ticket (← or k)"
            className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-[hsl(var(--secondary))] hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {position && (
            <span className="text-[0.65rem] text-muted-foreground tabular-nums px-1">
              {position}
            </span>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            title="Next ticket (→ or j)"
            className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-[hsl(var(--secondary))] hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <CategoryPill name={detail.category} />
        <span className={`pill ${color.bg} ${color.text}`}>
          <Icon className="h-3 w-3" /> {color.label}
        </span>
        {detail.classification?.confidence != null && (
          <span className="pill">conf {(detail.classification.confidence * 100).toFixed(0)}%</span>
        )}
      </div>

      {customerMismatch && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex items-center gap-3">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <div className="flex-1 min-w-0">
            Gorgias has this ticket attached to <span className="font-mono">{detail.gorgias_customer_email || '(none)'}</span>,
            but the real customer is <span className="font-semibold">{detail.customer_email}</span>.
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 shrink-0"
            disabled={fixingCustomer}
            onClick={onFixCustomer}
          >
            {fixingCustomer ? 'Fixing…' : 'Fix in Gorgias'}
          </Button>
        </div>
      )}

      {detail.merge_candidates && detail.merge_candidates.length > 0 && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 px-4 py-3 text-xs text-indigo-900">
          <div className="flex items-center gap-2 mb-2">
            <GitMerge className="h-3.5 w-3.5 shrink-0" />
            <span className="font-semibold">
              {detail.merge_candidates.length} other open ticket{detail.merge_candidates.length === 1 ? '' : 's'} from this customer
            </span>
          </div>
          <ul className="space-y-1.5">
            {detail.merge_candidates.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-mono text-[0.65rem] text-muted-foreground">#{c.gorgias_id}</span>
                  {' '}
                  <span className="truncate">{c.subject || '(no subject)'}</span>
                  {c.category && (
                    <span className="text-[0.6rem] text-muted-foreground"> · {c.category}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onMerge(c.id)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-indigo-300 bg-white px-2 py-1 text-[0.65rem] font-medium text-indigo-700 hover:bg-indigo-100"
                  title="Merge the current ticket into this one"
                >
                  <GitMerge className="h-3 w-3" />
                  Merge into
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {detail.suggested_agents_history && detail.suggested_agents_history.length > 0 && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-xs text-indigo-900">
          <div className="flex items-center gap-2 mb-2">
            <History className="h-3.5 w-3.5 shrink-0" />
            <span className="font-semibold">Previously handled by</span>
          </div>
          <ul className="space-y-1.5">
            {detail.suggested_agents_history.map((a) => (
              <li key={a.name} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-semibold text-indigo-900">{a.name}</span>
                  <span className="text-indigo-700">
                    {' · '}
                    {a.count} ticket{a.count === 1 ? '' : 's'}
                  </span>
                  {a.last_ticket_id && (
                    <>
                      {' · '}
                      <a
                        href={`https://lamptwist.gorgias.com/app/ticket/${a.last_ticket_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-indigo-700"
                      >
                        last ticket
                      </a>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onAssign(a.name)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-indigo-300 bg-white px-2 py-1 text-[0.65rem] font-medium text-indigo-700 hover:bg-indigo-100"
                  title={`Assign this Gorgias ticket to ${a.name}`}
                >
                  Assign
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <MessageThread messages={detail.messages} fallbackBody={detail.first_body} />


      {detail.endpoint_calls?.length > 0 && (
        <div>
          <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mb-2">Data endpoints</p>
          <div className="space-y-2">
            {detail.endpoint_calls.map((ec, i) => (
              <div key={i} className="rounded-xl border border-border px-3 py-2 flex items-center justify-between">
                <code className="text-xs text-foreground">{ec.endpoint}</code>
                <span className={`pill ${ec.status === 'implemented' ? 'bg-emerald-50 text-emerald-700' : ec.status === 'stub' ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`}>
                  {ec.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.draft_reply ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground">
                {detail.draft_audience === 'agent' ? 'Agent action note' : 'Draft reply'}
              </p>
              {detail.draft_audience === 'agent' && (
                <span
                  title="This is an internal note for the support agent, NOT a customer reply. Don't copy-paste into an email."
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold bg-indigo-50 text-indigo-700"
                >
                  for agent
                </span>
              )}
              {detail.draft_confidence != null && (
                <span
                  title={detail.draft_confidence_reason || 'self-reported confidence'}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${
                    detail.draft_confidence >= 0.9
                      ? 'bg-emerald-50 text-emerald-700'
                      : detail.draft_confidence >= 0.7
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-rose-50 text-rose-700'
                  }`}
                >
                  <Gauge className="h-3 w-3" />
                  {(detail.draft_confidence * 100).toFixed(0)}%
                </span>
              )}
            </div>
            {detail.dispatch_run_id && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={savingFeedback}
                  onClick={() =>
                    onFeedback(detail.human_feedback === 'correct' ? '' : 'correct')
                  }
                  className={`rounded-lg p-1.5 transition ${
                    detail.human_feedback === 'correct'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-muted-foreground hover:bg-[hsl(var(--secondary))]'
                  }`}
                  title="Mark as correct"
                >
                  <ThumbsUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  disabled={savingFeedback}
                  onClick={() => {
                    if (detail.human_feedback === 'incorrect') {
                      onFeedback('');
                      return;
                    }
                    const note = window.prompt(
                      'What is wrong with this draft? (optional)'
                    );
                    if (note === null) return;
                    onFeedback('incorrect', note || '');
                  }}
                  className={`rounded-lg p-1.5 transition ${
                    detail.human_feedback === 'incorrect'
                      ? 'bg-rose-100 text-rose-700'
                      : 'text-muted-foreground hover:bg-[hsl(var(--secondary))]'
                  }`}
                  title="Flag as incorrect"
                >
                  <ThumbsDown className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
          <div
            className={
              detail.draft_audience === 'agent'
                ? 'rounded-xl border border-indigo-300 bg-indigo-50/60 p-4 text-sm whitespace-pre-wrap'
                : 'rounded-xl border border-[hsl(var(--primary)/0.25)] bg-[hsl(var(--accent))] p-4 text-sm whitespace-pre-wrap'
            }
          >
            {renderDraftWithLinks(detail.draft_reply)}
          </div>
          {detail.human_feedback === 'incorrect' && detail.human_feedback_note && (
            <p className="mt-2 text-xs text-rose-700 italic">
              Flagged: {detail.human_feedback_note}
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          No draft yet. Run the dispatcher to generate one.
        </div>
      )}

      {detail.missing_reason && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          {detail.missing_reason}
        </div>
      )}

      {detail.suggested_macros?.length > 0 && (
        <div>
          <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mb-2">
            Suggested macros
          </p>
          <ul className="space-y-2">
            {detail.suggested_macros.map((m) => (
              <li
                key={m.id}
                className="rounded-xl border border-border px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Wand2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--primary))]" />
                    <span className="text-sm font-medium text-foreground truncate">{m.name}</span>
                    {m.category && <span className="pill text-[0.6rem]">{m.category}</span>}
                  </div>
                  <span className="text-[0.65rem] text-muted-foreground">
                    score {(m.score * 100).toFixed(0)}%
                  </span>
                </div>
                {m.body_text && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">
                    {m.body_text}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button onClick={onDispatch} disabled={dispatching} className="gap-2">
          <Sparkles className={`h-4 w-4 ${dispatching ? 'animate-pulse' : ''}`} />
          {dispatching ? 'Dispatching…' : 'Classify & dispatch'}
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() =>
            window.open(`https://lamptwist.gorgias.com/app/ticket/${detail.gorgias_id}`, '_blank', 'noopener')
          }
        >
          <ExternalLink className="h-4 w-4" />
          Open in Gorgias
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          onClick={onArchive}
          title="Archive locally in Replai (does not touch Gorgias)"
        >
          <Archive className="h-4 w-4" />
          Archive
        </Button>
        <Button
          variant="outline"
          className="gap-2 text-rose-700 hover:bg-rose-50"
          onClick={() => onBlockSender('domain')}
          title="Block this sender's entire domain. Future tickets from the domain auto-archive on ingest."
        >
          <Ban className="h-4 w-4" />
          Block sender
        </Button>
        <Button
          variant="outline"
          className="gap-2"
          disabled={posting || !detail.draft_reply || Boolean(detail.note_posted_at)}
          onClick={onPostNote}
          title={
            detail.note_posted_at
              ? `Already posted at ${new Date(detail.note_posted_at).toLocaleString()}`
              : 'Post as internal note on the Gorgias ticket (agents only)'
          }
        >
          <Send className={`h-4 w-4 ${posting ? 'animate-pulse' : ''}`} />
          {detail.note_posted_at
            ? 'Note posted ✓'
            : posting
            ? 'Posting…'
            : 'Post as note'}
        </Button>
      </div>

      {postError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          {postError}
        </div>
      )}
    </div>
  );
};

const IngestProgressPanel = ({ ingesting, events, summary, onClose, onCancel }) => {
  const logRef = useRef(null);

  useEffect(() => {
    // auto-scroll to bottom as events arrive
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events.length]);

  const ticketEvents = events.filter((e) => e.type === 'ticket');
  const errorEvents = events.filter((e) => e.type === 'error' || e.type === 'fatal');
  const created = ticketEvents.filter((e) => e.action === 'created').length;
  const updated = ticketEvents.filter((e) => e.action === 'updated').length;
  const processed = ticketEvents.length;

  const done = Boolean(summary);

  return (
    <div className="mb-6 rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-border/70 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {done ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          ) : (
            <RefreshCw className={`h-5 w-5 shrink-0 ${ingesting ? 'animate-spin text-[hsl(var(--primary))]' : 'text-muted-foreground'}`} />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">
              {done
                ? `Ingest complete - ${summary.created} created, ${summary.updated} updated`
                : ingesting
                ? `Ingesting… ${processed} processed`
                : 'Ingest paused'}
            </p>
            <p className="text-xs text-muted-foreground">
              <span className="text-emerald-700 font-medium">+{created} new</span>
              {' · '}
              <span className="text-amber-700 font-medium">{updated} updated</span>
              {errorEvents.length > 0 && (
                <>
                  {' · '}
                  <span className="text-rose-700 font-medium">{errorEvents.length} errors</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ingesting && (
            <Button variant="outline" size="sm" onClick={onCancel} className="gap-1">
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
            title="Hide"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <ul
        ref={logRef}
        className="divide-y divide-border/70 max-h-56 overflow-y-auto text-xs"
      >
        {events.length === 0 && (
          <li className="px-5 py-4 text-center text-muted-foreground">
            Starting ingest…
          </li>
        )}
        {events.map((e) => {
          if (e.type === 'start') {
            return (
              <li key={e._key} className="px-5 py-2 text-muted-foreground">
                Opening Gorgias view {e.view_id}…
              </li>
            );
          }
          if (e.type === 'ticket') {
            const Icon = e.action === 'created' ? Plus : RefreshCw;
            const color = e.action === 'created' ? 'text-emerald-700' : 'text-amber-700';
            return (
              <li key={e._key} className="px-5 py-2 flex items-center gap-3">
                <Icon className={`h-3.5 w-3.5 shrink-0 ${color}`} />
                <span className={`font-medium shrink-0 w-16 ${color}`}>
                  {e.action}
                </span>
                <span className="font-mono text-muted-foreground shrink-0">#{e.gorgias_id}</span>
                <span className="truncate text-foreground flex-1">
                  {e.subject || '(no subject)'}
                </span>
                <span className="text-muted-foreground shrink-0">
                  {e.customer_email}
                </span>
                <span className="text-[0.65rem] text-muted-foreground shrink-0">
                  {e.message_count} msg
                </span>
              </li>
            );
          }
          if (e.type === 'error' || e.type === 'fatal') {
            return (
              <li key={e._key} className="px-5 py-2 flex items-start gap-3 bg-rose-50/40">
                <AlertTriangle className="h-3.5 w-3.5 text-rose-700 shrink-0 mt-0.5" />
                <span className="font-mono text-rose-900 shrink-0">
                  {e.gorgias_id ? `#${e.gorgias_id}` : 'fatal'}
                </span>
                <span className="text-rose-800">{e.error}</span>
              </li>
            );
          }
          return null;
        })}
      </ul>
    </div>
  );
};

const DispatchAllProgressPanel = ({ dispatching, events, summary, onClose, onCancel }) => {
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events.length]);

  const runs = events.filter((e) => e.type === 'run');
  const skips = events.filter((e) => e.type === 'skip');
  const errors = events.filter((e) => e.type === 'error' || e.type === 'post_error' || e.type === 'fatal');
  const totalExpected = events.find((e) => e.type === 'start')?.total || 0;
  const startEvt = events.find((e) => e.type === 'start');
  const threshold = startEvt?.threshold;
  const autoPostRequested = Boolean(startEvt?.auto_post);

  const readyCount = runs.filter((r) => r.status === 'ready').length;
  const humanCount = runs.filter((r) => r.status === 'human').length;
  const eligibleCount = runs.filter((r) => r.eligible_to_post).length;
  const postedCount = runs.filter((r) => r.auto_posted).length;

  const done = Boolean(summary);

  return (
    <div className="mb-6 rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-border/70 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {done ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          ) : (
            <Zap className={`h-5 w-5 shrink-0 ${dispatching ? 'animate-pulse text-[hsl(var(--primary))]' : 'text-muted-foreground'}`} />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">
              {done
                ? `Dispatch complete - ${summary.ok} ok, ${summary.failed} failed${summary.skipped_existing ? `, ${summary.skipped_existing} skipped` : ''}${autoPostRequested ? `, ${summary.auto_posted} auto-posted` : ''}`
                : dispatching
                ? `Dispatching… ${runs.length + skips.length}/${totalExpected || '?'}`
                : 'Dispatch paused'}
            </p>
            <p className="text-xs text-muted-foreground">
              <span className="text-emerald-700 font-medium">{readyCount} ready</span>
              {' · '}
              <span className="text-indigo-700 font-medium">{humanCount} → human</span>
              {' · '}
              <span className="text-emerald-700 font-medium">{eligibleCount} meet threshold{typeof threshold === 'number' ? ` (≥${Math.round(threshold * 100)}%)` : ''}</span>
              {autoPostRequested && (
                <>
                  {' · '}
                  <span className="text-emerald-700 font-medium">{postedCount} pushed to Gorgias</span>
                </>
              )}
              {errors.length > 0 && (
                <>
                  {' · '}
                  <span className="text-rose-700 font-medium">{errors.length} errors</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {dispatching && (
            <Button variant="outline" size="sm" onClick={onCancel} className="gap-1">
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
            title="Hide"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <ul
        ref={logRef}
        className="divide-y divide-border/70 max-h-64 overflow-y-auto text-xs"
      >
        {events.length === 0 && (
          <li className="px-5 py-4 text-center text-muted-foreground">
            Starting dispatch…
          </li>
        )}
        {events.map((e) => {
          if (e.type === 'start') {
            return (
              <li key={e._key} className="px-5 py-2 text-muted-foreground">
                Starting {e.total} ticket{e.total === 1 ? '' : 's'} · push threshold ≥{Math.round((e.threshold || 0) * 100)}%
                {e.auto_post ? ' · auto-post ON' : ' · dry run (no Gorgias writes)'}
              </li>
            );
          }
          if (e.type === 'skip') {
            return (
              <li key={e._key} className="px-5 py-2 flex items-center gap-3 text-muted-foreground">
                <History className="h-3.5 w-3.5 shrink-0" />
                <span className="font-mono shrink-0">#{e.gorgias_id}</span>
                <span className="truncate flex-1">{e.subject || '(no subject)'}</span>
                <span className="shrink-0 text-[0.65rem] italic">{e.reason}</span>
              </li>
            );
          }
          if (e.type === 'run') {
            const statusColor = {
              ready: 'text-emerald-700',
              human: 'text-indigo-700',
              partial: 'text-amber-700',
              missing: 'text-stone-600',
              error: 'text-rose-700',
            }[e.status] || 'text-stone-600';
            return (
              <li key={e._key} className="px-5 py-2 flex items-center gap-3">
                <Sparkles className={`h-3.5 w-3.5 shrink-0 ${statusColor}`} />
                <span className={`font-medium shrink-0 w-14 ${statusColor}`}>{e.status}</span>
                <span className="font-mono text-muted-foreground shrink-0">#{e.gorgias_id}</span>
                <span className="truncate text-foreground flex-1">{e.subject || '(no subject)'}</span>
                {e.triage_escalated && (
                  <span className="inline-flex items-center gap-0.5 text-[0.65rem] text-indigo-700 shrink-0">
                    <ShieldAlert className="h-3 w-3" />
                    triage
                  </span>
                )}
                {e.selection_source === 'llm' && e.tool_count > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[0.65rem] text-emerald-700 shrink-0">
                    <Bot className="h-3 w-3" />
                    {e.tool_count}
                  </span>
                )}
                {typeof e.draft_confidence === 'number' && (
                  <span className={`font-mono text-[0.65rem] shrink-0 ${e.eligible_to_post ? 'text-emerald-700 font-semibold' : 'text-muted-foreground'}`}>
                    {Math.round(e.draft_confidence * 100)}%
                  </span>
                )}
                {e.auto_posted && (
                  <span className="inline-flex items-center gap-0.5 text-[0.65rem] text-emerald-700 font-semibold shrink-0">
                    <Send className="h-3 w-3" />
                    pushed
                  </span>
                )}
                {!e.auto_posted && autoPostRequested && !e.eligible_to_post && (
                  <span className="text-[0.65rem] text-stone-500 shrink-0" title={e.eligible_reason}>
                    skip
                  </span>
                )}
              </li>
            );
          }
          if (e.type === 'error' || e.type === 'post_error' || e.type === 'fatal') {
            return (
              <li key={e._key} className="px-5 py-2 flex items-start gap-3 bg-rose-50/40">
                <AlertTriangle className="h-3.5 w-3.5 text-rose-700 shrink-0 mt-0.5" />
                <span className="font-mono text-rose-900 shrink-0">
                  {e.gorgias_id ? `#${e.gorgias_id}` : e.type}
                </span>
                <span className="text-rose-800">{e.error}</span>
              </li>
            );
          }
          return null;
        })}
      </ul>
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Renders a draft reply with markdown-style [label](url) links converted
// to real anchors. Anything else is rendered as preserved-whitespace text.
const renderDraftWithLinks = (text) => {
  if (!text) return null;
  const linkRe = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index));
    }
    parts.push(
      <a
        key={`lnk-${key++}`}
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[hsl(var(--primary))] underline hover:opacity-80"
      >
        {m[1]}
      </a>
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffSec = (Date.now() - d.getTime()) / 1000;
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const MessageThread = ({ messages, fallbackBody }) => {
  if (!messages || messages.length === 0) {
    if (!fallbackBody) return null;
    return (
      <div className="rounded-xl bg-[hsl(var(--secondary))] p-4">
        <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mb-1">Customer message</p>
        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{fallbackBody}</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mb-2">
        Thread · {messages.length} message{messages.length === 1 ? '' : 's'}
      </p>
      <ol className="space-y-3">
        {messages.map((m) => {
          const Icon = m.from_agent ? Headset : User;
          const who = m.from_agent ? 'LampTwist' : (m.sender_name || m.sender_email || 'Customer');
          return (
            <li
              key={m.id}
              className={`rounded-xl p-4 border ${
                m.from_agent
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--primary)/0.25)]'
                  : 'bg-[hsl(var(--secondary))] border-border'
              }`}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                  <Icon className="h-3.5 w-3.5" />
                  <span>{who}</span>
                  {m.from_agent && (
                    <span className="pill text-[0.6rem]">agent</span>
                  )}
                </div>
                <span className="text-[0.65rem] text-muted-foreground">{formatDate(m.sent_at)}</span>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {m.body || <span className="italic text-muted-foreground">(empty message)</span>}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default Inbox;
